/**
 * Hook TanStack Query per il dominio Report SRP e timer SLA (E10.6/E10.7).
 * Pattern identico a features/triage/hooks.ts (slice 3).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchSrpReports,
  fetchSrpReport,
  createSrpReport,
  saveThread,
  setReportCategory,
  recordReportStep,
  undoReportStep,
  resolveThread,
  attachReportCve,
  detachReportCve,
  fetchSlaTimers,
  correctSlaDetection,
} from './api.js';
import type {
  SrpReport,
  CreateSrpReportInput,
  SaveThreadInput,
  NotificationStep,
  ThreadStatus,
  SlaTimer,
  CorrectSlaDetectionInput,
  AttachCveInput,
} from './types.js';
import type { ReportCategory } from './category.js';

/** Chiavi query centralizzate per il dominio SRP. */
export const srpKeys = {
  all: ['srp'] as const,
  reports: () => ['srp', 'reports'] as const,
  report: (id: number) => ['srp', 'report', id] as const,
  slaTimers: () => ['srp', 'sla-timers'] as const,
  /** Prefisso dello storico audit di un report (vedi auditHistory.ts); senza id = tutti. */
  auditHistory: (id?: number) =>
    id === undefined
      ? (['srp', 'audit-history'] as const)
      : (['srp', 'audit-history', id] as const),
} as const;

/** Dopo una mutazione del report: lista, dettaglio e storico audit vanno riletti. */
function invalidaReport(queryClient: QueryClient, id: number): void {
  void queryClient.invalidateQueries({ queryKey: srpKeys.reports() });
  void queryClient.invalidateQueries({ queryKey: srpKeys.report(id) });
  void queryClient.invalidateQueries({ queryKey: srpKeys.auditHistory(id) });
}

// ---------------------------------------------------------------------------
// Report SRP
// ---------------------------------------------------------------------------

/** Recupera la lista dei report SRP. */
export function useSrpReports(): UseQueryResult<SrpReport[]> {
  return useQuery({
    queryKey: srpKeys.reports(),
    queryFn: fetchSrpReports,
  });
}

/** Recupera il dettaglio di un report SRP per ID. Abilitato solo se id > 0. */
export function useSrpReport(id: number): UseQueryResult<SrpReport> {
  return useQuery({
    queryKey: srpKeys.report(id),
    queryFn: () => fetchSrpReport(id),
    enabled: id > 0,
  });
}

/** Mutation per creare un nuovo report SRP. Invalida la lista dopo il successo. */
export function useCreateSrpReport(): UseMutationResult<SrpReport, Error, CreateSrpReportInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSrpReport,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: srpKeys.reports() });
    },
  });
}

/**
 * Mutation per aggiornare note libere e/o checklist del thread di notifica.
 * Invalida lista e dettaglio dopo il successo.
 */
export function useSaveThread(id: number): UseMutationResult<SrpReport, Error, SaveThreadInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => saveThread(id, input),
    onSuccess: () => {
      invalidaReport(queryClient, id);
    },
  });
}

/**
 * Mutation per spostare il report tra le categorie ENISA.
 * Invalida lista e dettaglio dopo il successo.
 */
export function useSetReportCategory(
  id: number,
): UseMutationResult<SrpReport, Error, ReportCategory> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (category) => setReportCategory(id, category),
    onSuccess: () => {
      invalidaReport(queryClient, id);
    },
  });
}

/**
 * Mutation per registrare uno step CRA Art.14(2) come fatto, con nota facoltativa.
 * Invalida lista e dettaglio dopo il successo.
 */
export function useRecordStep(
  id: number,
): UseMutationResult<SrpReport, Error, { step: NotificationStep; note?: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ step, note }) => recordReportStep(id, step, note),
    onSuccess: () => {
      invalidaReport(queryClient, id);
      // La registrazione ferma il timer dello step: anche lo stato SLA dei timer cambia.
      void queryClient.invalidateQueries({ queryKey: srpKeys.slaTimers() });
    },
  });
}

/**
 * Mutation per annullare la registrazione di uno step (il timer riparte). Invalida report,
 * storico e timer SLA.
 */
export function useUndoStep(
  id: number,
): UseMutationResult<SrpReport, Error, { step: NotificationStep; reason: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ step, reason }) => undoReportStep(id, step, reason),
    onSuccess: () => {
      invalidaReport(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: srpKeys.slaTimers() });
    },
  });
}

/**
 * Mutation per impostare la risoluzione del thread di notifica (inclusa la riapertura di un
 * report precedente, `open`). Invalida lista, dettaglio e storico: dopo una riapertura il
 * chiamante resta sul dettaglio.
 */
export function useResolveThread(id: number): UseMutationResult<SrpReport, Error, ThreadStatus> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resolution) => resolveThread(id, resolution),
    onSuccess: () => {
      invalidaReport(queryClient, id);
    },
  });
}

/** Mutation per allegare una CVE al report. Invalida lista, dettaglio e storico. */
export function useAttachCve(id: number): UseMutationResult<SrpReport, Error, AttachCveInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => attachReportCve(id, input),
    onSuccess: () => {
      invalidaReport(queryClient, id);
    },
  });
}

/** Mutation per rimuovere una CVE dal report. Invalida lista, dettaglio e storico. */
export function useDetachCve(id: number): UseMutationResult<SrpReport, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cveId) => detachReportCve(id, cveId),
    onSuccess: () => {
      invalidaReport(queryClient, id);
    },
  });
}

// ---------------------------------------------------------------------------
// Timer SLA
// ---------------------------------------------------------------------------

/** Recupera la lista dei timer SLA con countdown calcolato. */
export function useSlaTimers(): UseQueryResult<SlaTimer[]> {
  return useQuery({
    queryKey: srpKeys.slaTimers(),
    queryFn: fetchSlaTimers,
    // Aggiorna ogni 60 secondi per mantenere il countdown aggiornato
    refetchInterval: 60_000,
  });
}

/**
 * Mutation per correggere l'ancora del clock CRA di un timer SLA. Invalida i timer (e quindi
 * le scadenze mostrate in lista, nel dettaglio e nella colonna prodotti).
 */
export function useCorrectSlaDetection(): UseMutationResult<
  SlaTimer,
  Error,
  { timerId: number } & CorrectSlaDetectionInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ timerId, ...input }) => correctSlaDetection(timerId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: srpKeys.slaTimers() });
      // La correzione è nello storico audit del report (evento sla_timer).
      void queryClient.invalidateQueries({ queryKey: srpKeys.auditHistory() });
    },
  });
}
